import React, {ReactNode} from 'react'
import {AdminPagePropsSet, createAdminPage} from '../../shared/hoc/createAdminPage'
import {EquipmentsRepository} from '../../equipments/EquipmentsRepository'
import FacilityModel, {NewFacilityModelObserver} from '../FacilityModel'
import {
    PageFooterArea,
    PageFotterAreaLeft,
    PageFotterAreaRight,
    PageMainArea,
} from '../../shared/hoc/withAdminPageTemplate'
import {AdminPrimaryButton} from '../../../styledComponents/adminPage/Buttons'
import {BackLink} from '../../../styledComponents/adminPage/Links'
import {Messages} from '../../../Messages'
import FacilitiesRepository from '../FacilitiesRepository'
import {Nullable} from '../../../app/TypeHelpers'
import NewFacilityForOvernightRental from '../NewFacilityForOvernightRental'
import {DetailsWithInformation} from '../../../network/response/DetailsWithInformation'
import FacilityForm from './FacilityForm'
import NewFacilityForDurationRental from '../NewFacilityForDurationRental'
import {AsyncFunction} from '../../shared/hoc/withAsyncCommunication'
import NatCondominiumRepo from '../../../__NoAutomatedTests/components/condominium/repository/NatCondominiumRepo'

interface Props {
    condominiumCd: string
    natCondominiumRepo: NatCondominiumRepo
    equipmentsRepo: EquipmentsRepository
    facilitiesRepo: FacilitiesRepository
    facilityModel: FacilityModel
}

interface State {
    isEnableToRegister: boolean
}

class AdminRegisterFacilityPage
    extends React.Component<Props & AdminPagePropsSet, State>
    implements NewFacilityModelObserver {

    static displayName: string = 'AdminRegisterFacilityPage'

    constructor(props: Readonly<Props & AdminPagePropsSet>) {
        super(props)

        this.state = {
            isEnableToRegister: false,
        }

        this.handleRegisterButtonClick = this.handleRegisterButtonClick.bind(this)
        this.handleRegisterOkClick = this.handleRegisterOkClick.bind(this)
        this.handleRegisterCancelClick = this.handleRegisterCancelClick.bind(this)
    }

    onDataSetStateChange(completeToCreate: boolean): void {
        this.setState({
            isEnableToRegister: completeToCreate,
        })
    }

    onBuildOvernightRentalFacility(newFacility: NewFacilityForOvernightRental): Promise<void> {
        return this.registerFacility(
            this.props.facilitiesRepo.registerOvernightRentalFacility.bind(
                this.props.facilitiesRepo,
                this.props.condominiumCd,
                newFacility,
            ),
        )
    }

    onBuildDurationRentalFacility(newFacility: NewFacilityForDurationRental): Promise<void> {
        return this.registerFacility(
            this.props.facilitiesRepo.registerDurationRentalFacility.bind(
                this.props.facilitiesRepo,
                this.props.condominiumCd,
                newFacility,
            ),
        )
    }

    async registerFacility(
        registerFacilityFunction: AsyncFunction<any, DetailsWithInformation<number>>,
    ): Promise<void> {
        let facilityAndInformation: Nullable<DetailsWithInformation<number>> = null

        await this.props.eventEmitter.showLoading()
        try {
            facilityAndInformation = await this.props.asyncCallFunction(
                registerFacilityFunction,
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                },
                {defaultErrorTitle: Messages.EOPCA0001.toString('施設')},
            )
        } finally {
            this.props.eventEmitter.closeLoading()
        }

        if (facilityAndInformation) {
            const {condominiumCd} = this.props
            this.props.navigate(
                `/admin/condominiums/${condominiumCd}/facilities/${facilityAndInformation.details}`,
                {
                    state: {
                        initialMessage: {
                            title: facilityAndInformation.information.title,
                            messageLevel: facilityAndInformation.information.type,
                            detail: facilityAndInformation.information.message,
                        },
                    },
                },
            )
        }
    }

    handleRegisterOkClick(): void {
        this.props.eventEmitter.closeDialog()
        this.props.eventEmitter.clearBroadcastedErrors()
        this.props.facilityModel.build()
    }

    handleRegisterCancelClick(): void {
        this.props.eventEmitter.closeDialog()
    }

    handleRegisterButtonClick(): void {
        this.props.eventEmitter.showDialog({
                title: '確認',
                errorLevel: Messages.IOPCC0012.errorLevel,
                message: Messages.IOPCC0012.toString('施設'),
                buttonProps: [
                    {
                        isDefault: false,
                        buttonLabel: 'キャンセル',
                        buttonHandler: this.handleRegisterCancelClick,
                    },
                    {
                        isDefault: false,
                        buttonLabel: 'OK',
                        buttonHandler: this.handleRegisterOkClick,
                    },
                ],
            },
        )
    }

    renderFooterArea(): ReactNode {
        return (
            <>
                <PageFotterAreaRight>
                    <AdminPrimaryButton
                        disabled={!this.state.isEnableToRegister}
                        onClick={this.handleRegisterButtonClick}
                    >
                        登録
                    </AdminPrimaryButton>
                </PageFotterAreaRight>
                <PageFotterAreaLeft>
                    <BackLink
                        to={`/admin/condominiums/${this.props.condominiumCd}/facilities/search`}>
                        施設検索へ戻る
                    </BackLink>
                </PageFotterAreaLeft>
            </>
        )
    }

    render(): ReactNode {
        return (
            <>
                <PageMainArea>
                    <FacilityForm
                        facilityId={null}
                        newFacilityModelObserver={this}
                        {...this.props}
                    />
                </PageMainArea>

                <PageFooterArea>
                    {this.renderFooterArea()}
                </PageFooterArea>
            </>
        )
    }
}

export default createAdminPage(AdminRegisterFacilityPage)
